import { DesignTheme, TextStyleTokens } from "../types";
import { ALERT_COLORS } from "./alertPalette";

const textStyles: TextStyleTokens = {
  d1: {
    fontSize: "clamp(44px, 6.2vw, 76px)",
    fontWeight: 750,
    letterSpacing: "-0.045em",
    lineHeight: 0.98,
  },
  d2: {
    fontSize: "clamp(32px, 4vw, 48px)",
    fontWeight: 720,
    letterSpacing: "-0.04em",
    lineHeight: 1.04,
  },
  h2: {
    fontSize: "clamp(26px, 3vw, 36px)",
    fontWeight: 700,
    letterSpacing: "-0.035em",
    lineHeight: 1.08,
  },
  h3: {
    fontSize: "19px",
    fontWeight: 680,
    letterSpacing: "-0.02em",
    lineHeight: 1.2,
  },
  h4: {
    fontSize: "15px",
    fontWeight: 650,
    letterSpacing: "-0.012em",
    lineHeight: 1.2,
  },
  bodyLg: {
    fontSize: "17.5px",
    fontWeight: 400,
    letterSpacing: "-0.008em",
    lineHeight: 1.6,
  },
  body: {
    fontSize: "14.5px",
    fontWeight: 400,
    letterSpacing: "normal",
    lineHeight: 1.6,
  },
  small: {
    fontSize: "12.5px",
    fontWeight: 400,
    letterSpacing: "normal",
    lineHeight: 1.55,
  },
  eyebrow: {
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: "0.16em",
    lineHeight: 1,
    mono: true,
  },
  mono: {
    fontSize: "12px",
    fontWeight: 400,
    letterSpacing: "0.02em",
    lineHeight: 1,
    mono: true,
  },
};

export const spectrumTheme: DesignTheme = {
  id: "spectrum",
  name: "Spectrum",
  mode: "light",
  color: {
    canvas: "#ffffff",
    surface: "#f7f7f6",
    ink: "#0d0d0f",
    inkMuted: "#4b4b52",
    inkSubtle: "#8b8b92",
    accent: "#0d0d0f",
    accentInk: "#ffffff",
    border: "#e8e8e6",
    borderStrong: "#d9d9d6",
    hover: "#f1f1ef",
    inverse: "#0d0d0f",
    inverseInk: "#ffffff",
    danger: "#c2452d",
    success: "#10B981",
    warning: "#F59E0B",
    alert: ALERT_COLORS,
    photoOverlay:
      "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)",
    immersive: "#0e0d12",
    immersiveInk: "#f4f3f6",
    night: {
      canvas: "#0e0d12",
      surface: "#17161d",
      ink: "#f4f3f6",
      inkMuted: "rgba(244, 243, 246, 0.6)",
      inkSubtle: "rgba(244, 243, 246, 0.36)",
      border: "rgba(244, 243, 246, 0.1)",
    },
  },
  accentDots: ["#ffcf7d", "#fea67d", "#be9dfd", "#01dafe", "#e7f09a"],
  gradient: {
    spectrum:
      "linear-gradient(100deg, #ffcf7d 0%, #fea67d 32%, #be9dfd 68%, #01dafe 100%)",
    spectrumSoft:
      "linear-gradient(100deg, rgba(255,207,125,0.14), rgba(254,166,125,0.1) 32%, rgba(190,157,253,0.12) 68%, rgba(1,218,254,0.1) 100%)",
    accentDeepA: "#e97a4a",
    accentDeepB: "#8b5cf6",
  },
  radius: {
    sm: 8,
    md: 12,
    lg: 18,
    xl: 26,
    pill: 999,
  },
  space: {
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 24,
    6: 32,
    7: 48,
    8: 64,
  },
  type: {
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontFamilyMono: "'IBM Plex Mono', monospace",
    weightRegular: 400,
    weightMedium: 500,
    weightSemibold: 600,
    weightBold: 700,
    sizeXs: 12,
    sizeSm: 13,
    sizeMd: 15,
    sizeLg: 19,
    sizeXl: 28,
    sizeDisplay: 44,
    lineTight: 0.98,
    lineNormal: 1.5,
    textStyles,
  },
  shadow: {
    none: "none",
    soft: "0 1px 2px rgba(13, 13, 15, 0.05), 0 0 0 1px rgba(13, 13, 15, 0.04)",
    lift: "0 2px 6px rgba(13, 13, 15, 0.06), 0 12px 32px rgba(13, 13, 15, 0.08)",
    deep: "0 4px 12px rgba(13, 13, 15, 0.08), 0 28px 64px rgba(13, 13, 15, 0.14)",
    night: "0 30px 80px rgba(0, 0, 0, 0.5)",
  },
  motion: {
    fast: "140ms",
    normal: "260ms",
    slow: "520ms",
    ease: "cubic-bezier(0.4, 0, 0.2, 1)",
    easeOut: "cubic-bezier(0.16, 1, 0.3, 1)",
  },
  blur: {
    sm: "8px",
    md: "14px",
    lg: "18px",
  },
};

/**
 * Dark companion — flips canvas/surface to the "night" register that on
 * the light pack only appears contextually (product-theater bands).
 * Ready for a future dark-mode toggle; not used by default.
 */
export const spectrumDarkTheme: DesignTheme = {
  ...spectrumTheme,
  id: "spectrum-dark",
  name: "Spectrum Dark",
  mode: "dark",
  color: {
    ...spectrumTheme.color,
    canvas: "#0e0d12",
    surface: "#17161d",
    ink: "#f4f3f6",
    inkMuted: "rgba(244, 243, 246, 0.6)",
    inkSubtle: "rgba(244, 243, 246, 0.36)",
    accent: "#f4f3f6",
    accentInk: "#0e0d12",
    border: "rgba(244, 243, 246, 0.1)",
    borderStrong: "rgba(244, 243, 246, 0.18)",
    hover: "rgba(244, 243, 246, 0.06)",
    inverse: "#f4f3f6",
    inverseInk: "#0e0d12",
    photoOverlay:
      "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.7) 100%)",
    immersive: "#000000",
    immersiveInk: "#ffffff",
  },
  shadow: {
    ...spectrumTheme.shadow,
    soft: "0 8px 24px rgba(0, 0, 0, 0.35)",
    lift: "0 16px 40px rgba(0, 0, 0, 0.5)",
  },
};
